// File Metadata:
// Filename: pointsRewardsRtkApi.js
// Version: 2.00
// Created: 2025-06-29 00:00:00
// Last Modified: 2026-03-22
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: RTK Query API slice for the Rewards feature — rewards CRUD, redemptions,
//              points management, group goals, and PIN operations.

import { baseApi } from '../../../store/api/baseApi';

// ── Constants ────────────────────────────────────────────────

export const POINT_VALUES = {
  CHORE_COMPLETED: 10,
  CHECKLIST_COMPLETED: 15,
  TODO_COMPLETED: 5,
  STREAK_BONUS: 5,
  WEEKLY_GOAL_MET: 25,
  MONTHLY_GOAL_MET: 100,
  HELPING_FAMILY_MEMBER: 8,
  TASK_OVERDUE_PENALTY: -5,
  MISSED_RESPONSIBILITY: -10
};

export const POINT_REASONS = {
  CHORE_COMPLETED: 'Completed chore',
  CHECKLIST_COMPLETED: 'Completed checklist',
  TODO_COMPLETED: 'Completed todo item',
  STREAK_BONUS: 'Streak bonus',
  WEEKLY_GOAL_MET: 'Weekly goal achieved',
  MONTHLY_GOAL_MET: 'Monthly goal achieved',
  HELPING_FAMILY_MEMBER: 'Helped family member',
  TASK_OVERDUE_PENALTY: 'Task overdue penalty',
  MISSED_RESPONSIBILITY: 'Missed responsibility'
};

export const REWARD_TYPES = {
  DIGITAL: 'digital',
  PHYSICAL: 'physical',
  SCREEN_TIME: 'screen_time',
  GOAL: 'goal'
};

export const REWARD_TYPE_LABELS = {
  digital: 'Digital',
  physical: 'Physical',
  screen_time: 'Screen Time',
  goal: 'Group Goal'
};

// ── API Slice ────────────────────────────────────────────────

export const pointsRewardsRtkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // ─── Rewards CRUD ──────────────────────────────────────

    getRewards: builder.query({
      query: (params = {}) => ({
        url: '/rewards',
        params,
      }),
      transformResponse: (response) => response.data || response,
      providesTags: ['Reward'],
      keepUnusedDataFor: 300,
    }),

    getRewardById: builder.query({
      query: (id) => `/rewards/${id}`,
      transformResponse: (response) => response.data || response,
      providesTags: (result, error, id) => [{ type: 'Reward', id }],
    }),

    createReward: builder.mutation({
      query: (rewardData) => ({
        url: '/rewards',
        method: 'POST',
        body: rewardData,
      }),
      invalidatesTags: ['Reward'],
      async onQueryStarted(rewardData, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          pointsRewardsRtkApi.util.updateQueryData('getRewards', undefined, (draft) => {
            const optimistic = {
              _id: `temp-${Date.now()}`,
              ...rewardData,
              isActive: true,
              status: rewardData.type === 'goal' ? 'active' : 'available',
              contributions: [],
              totalContributed: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            draft.unshift(optimistic);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    updateReward: builder.mutation({
      query: ({ id, ...rewardData }) => ({
        url: `/rewards/${id}`,
        method: 'PUT',
        body: rewardData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Reward', id },
        'Reward'
      ],
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          pointsRewardsRtkApi.util.updateQueryData('getRewards', undefined, (draft) => {
            const reward = draft.find(r => r._id === id);
            if (reward) Object.assign(reward, { ...patch, updatedAt: new Date().toISOString() });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteReward: builder.mutation({
      query: (id) => ({
        url: `/rewards/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Reward'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          pointsRewardsRtkApi.util.updateQueryData('getRewards', undefined, (draft) => {
            return draft.filter(r => r._id !== id);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // ─── Redemptions ───────────────────────────────────────

    redeemReward: builder.mutation({
      query: ({ rewardId }) => ({
        url: '/rewards/redeem',
        method: 'POST',
        body: { rewardId },
      }),
      invalidatesTags: ['Reward', 'Redemption', 'User', 'Profile', 'Activity'],
      async onQueryStarted({ rewardId }, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        const rewardsData = pointsRewardsRtkApi.endpoints.getRewards.select()(state);
        const reward = rewardsData.data?.find(r => r._id === rewardId);

        let userPatch, rewardPatch;
        if (reward) {
          userPatch = dispatch(
            baseApi.util.updateQueryData('getMe', undefined, (draft) => {
              if (draft && typeof draft.points === 'number') {
                draft.points -= reward.points;
              }
            })
          );
          rewardPatch = dispatch(
            pointsRewardsRtkApi.util.updateQueryData('getRewards', undefined, (draft) => {
              const r = draft.find(r => r._id === rewardId);
              if (r && r.supply !== null) r.supply -= 1;
            })
          );
        }
        try {
          await queryFulfilled;
        } catch {
          userPatch?.undo();
          rewardPatch?.undo();
        }
      },
    }),

    getRedemptions: builder.query({
      query: (params = {}) => ({
        url: '/rewards/redemptions',
        params,
      }),
      transformResponse: (response) => response.data || response,
      providesTags: ['Redemption'],
      keepUnusedDataFor: 120,
    }),

    approveRedemption: builder.mutation({
      query: ({ id, status, notes }) => ({
        url: `/rewards/redeem/${id}`,
        method: 'PUT',
        body: { status, notes },
      }),
      invalidatesTags: ['Redemption', 'Reward', 'User', 'Profile'],
    }),

    // ─── Points Management ─────────────────────────────────

    getPoints: builder.query({
      query: (userId) => `/points/${userId}`,
      transformResponse: (response) => response.data || response,
      providesTags: (result, error, userId) => [
        { type: 'Profile', id: userId },
        'Activity'
      ],
      keepUnusedDataFor: 120,
    }),

    awardPointsToChild: builder.mutation({
      query: ({ userId, points, reason }) => ({
        url: '/points/award',
        method: 'POST',
        body: { userId, points, reason },
      }),
      invalidatesTags: ['User', 'Profile', 'Activity'],
    }),

    // Legacy: award points to self (via profile endpoint)
    awardPoints: builder.mutation({
      query: ({ points, reason }) => ({
        url: '/profile/points/increment',
        method: 'PATCH',
        body: { points, reason },
      }),
      invalidatesTags: ['User', 'Profile', 'Activity'],
      async onQueryStarted({ points }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getMe', undefined, (draft) => {
            if (draft && typeof draft.points === 'number') {
              draft.points += points;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    setAbsolutePoints: builder.mutation({
      query: ({ points }) => ({
        url: '/profile/points',
        method: 'PUT',
        body: { points },
      }),
      invalidatesTags: ['User', 'Profile', 'Activity'],
      async onQueryStarted({ points }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getMe', undefined, (draft) => {
            if (draft) draft.points = points;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getPointsHistory: builder.query({
      query: (params = {}) => ({
        url: '/profile/points/history',
        params,
      }),
      providesTags: ['Activity'],
      keepUnusedDataFor: 120,
    }),

    // ─── Group Goal Rewards ────────────────────────────────

    getGroupGoalRewards: builder.query({
      query: () => '/group-goal-rewards',
      transformResponse: (response) => response.data || response,
      providesTags: ['Reward', 'GroupGoal'],
      keepUnusedDataFor: 300,
    }),

    createGroupGoalReward: builder.mutation({
      query: (goalData) => ({
        url: '/group-goal-rewards',
        method: 'POST',
        body: goalData,
      }),
      invalidatesTags: ['Reward', 'GroupGoal'],
    }),

    updateGroupGoalReward: builder.mutation({
      query: ({ id, ...goalData }) => ({
        url: `/group-goal-rewards/${id}`,
        method: 'PUT',
        body: goalData,
      }),
      invalidatesTags: ['Reward', 'GroupGoal'],
    }),

    contributeToGoal: builder.mutation({
      query: ({ goalId, points }) => ({
        url: `/group-goal-rewards/${goalId}/contribute`,
        method: 'POST',
        body: { points },
      }),
      invalidatesTags: ['Reward', 'GroupGoal', 'User', 'Profile', 'Activity'],
      async onQueryStarted({ goalId, points }, { dispatch, queryFulfilled }) {
        const userPatch = dispatch(
          baseApi.util.updateQueryData('getMe', undefined, (draft) => {
            if (draft && typeof draft.points === 'number') {
              draft.points -= points;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          userPatch.undo();
        }
      },
    }),

    // ─── PIN Management ────────────────────────────────────

    setPin: builder.mutation({
      query: ({ pin }) => ({
        url: '/rewards/pin/set',
        method: 'POST',
        body: { pin },
      }),
    }),

    verifyPin: builder.mutation({
      query: ({ pin, redemptionId }) => ({
        url: '/rewards/pin/verify',
        method: 'POST',
        body: { pin, redemptionId },
      }),
      invalidatesTags: ['Redemption'],
    }),

    // ─── Achievements & Stats ──────────────────────────────

    getAchievements: builder.query({
      query: () => '/profile/achievements',
      providesTags: ['Activity'],
      keepUnusedDataFor: 300,
    }),

    getLeaderboard: builder.query({
      query: (params = {}) => ({
        url: '/profile/leaderboard',
        params,
      }),
      providesTags: ['Users'],
      keepUnusedDataFor: 180,
    }),

    getUserStats: builder.query({
      query: (userId) => `/profile/stats${userId ? `/${userId}` : ''}`,
      providesTags: (result, error, userId) => [
        { type: 'Profile', id: userId || 'current' },
        'Activity'
      ],
      keepUnusedDataFor: 300,
    }),
  }),
});

// ── Exported Hooks ───────────────────────────────────────────

export const {
  // Rewards CRUD
  useGetRewardsQuery,
  useGetRewardByIdQuery,
  useCreateRewardMutation,
  useUpdateRewardMutation,
  useDeleteRewardMutation,

  // Redemptions
  useRedeemRewardMutation,
  useGetRedemptionsQuery,
  useApproveRedemptionMutation,

  // Points
  useGetPointsQuery,
  useAwardPointsToChildMutation,
  useAwardPointsMutation,
  useSetAbsolutePointsMutation,
  useGetPointsHistoryQuery,

  // Group goals
  useGetGroupGoalRewardsQuery,
  useCreateGroupGoalRewardMutation,
  useUpdateGroupGoalRewardMutation,
  useContributeToGoalMutation,

  // PIN
  useSetPinMutation,
  useVerifyPinMutation,

  // Achievements & Stats
  useGetAchievementsQuery,
  useGetLeaderboardQuery,
  useGetUserStatsQuery,
} = pointsRewardsRtkApi;

// ── Convenience Hook ─────────────────────────────────────────

export const usePointsActions = () => {
  const [awardPoints] = useAwardPointsMutation();

  return {
    awardChoreCompletion: () =>
      awardPoints({ points: POINT_VALUES.CHORE_COMPLETED, reason: POINT_REASONS.CHORE_COMPLETED }),
    awardChecklistCompletion: () =>
      awardPoints({ points: POINT_VALUES.CHECKLIST_COMPLETED, reason: POINT_REASONS.CHECKLIST_COMPLETED }),
    awardTodoCompletion: () =>
      awardPoints({ points: POINT_VALUES.TODO_COMPLETED, reason: POINT_REASONS.TODO_COMPLETED }),
    awardStreakBonus: () =>
      awardPoints({ points: POINT_VALUES.STREAK_BONUS, reason: POINT_REASONS.STREAK_BONUS }),
    awardWeeklyGoal: () =>
      awardPoints({ points: POINT_VALUES.WEEKLY_GOAL_MET, reason: POINT_REASONS.WEEKLY_GOAL_MET }),
    awardMonthlyGoal: () =>
      awardPoints({ points: POINT_VALUES.MONTHLY_GOAL_MET, reason: POINT_REASONS.MONTHLY_GOAL_MET }),
    awardHelpingFamily: () =>
      awardPoints({ points: POINT_VALUES.HELPING_FAMILY_MEMBER, reason: POINT_REASONS.HELPING_FAMILY_MEMBER }),
    penalizeOverdueTask: () =>
      awardPoints({ points: POINT_VALUES.TASK_OVERDUE_PENALTY, reason: POINT_REASONS.TASK_OVERDUE_PENALTY }),
    penalizeMissedResponsibility: () =>
      awardPoints({ points: POINT_VALUES.MISSED_RESPONSIBILITY, reason: POINT_REASONS.MISSED_RESPONSIBILITY }),
  };
};

export default pointsRewardsRtkApi;
